import { motion } from 'framer-motion';
import { useRevealViewport } from '../useRevealViewport';
import type { CinematicPalette } from './themePalettes';
import type { AdaptiveRevealConfig } from './adaptive/adaptiveConfig';
import { useTiltParallax } from './adaptive/useTiltParallax';
import {
  BACKDROP_REGISTRY,
  BaseGradientLayer,
  VignetteLayer,
  FilmGrainLayer,
} from './backdrops/BackdropLayers';
import type { BackdropLayerId } from './adaptive/revealStyles';

interface AtmosphereLayerProps {
  palette: CinematicPalette;
  /** When provided, picks backdrop layers and density from the adaptive config */
  config?: AdaptiveRevealConfig;
  /** Fade out the entire atmosphere when the scene takes over */
  fadeOut?: boolean;
}

/**
 * Adaptive cinematic atmosphere stack.
 *
 * If `config` is provided, composes the layer set chosen by the style preset
 * (e.g. ethereal_memory uses fog + dust + bokeh + lightStreaks + grain).
 * Mobile auto-trims to the first 3 layers for performance.
 *
 * Without `config`, falls back to a default fog + dust + lightStreaks + grain.
 */
export function AtmosphereLayer({ palette, config, fadeOut = false }: AtmosphereLayerProps) {
  const { isMobile } = useRevealViewport();

  const layers: BackdropLayerId[] = config
    ? config.style.backdropLayers
    : ['fog', 'dust', 'lightStreaks', 'filmGrain'];
  const trimmed = isMobile ? layers.slice(0, 3) : layers;

  const density = (config?.particleDensity ?? 1);
  const hueShift = config?.hueShift ?? 0;
  // Provide an rng even in fallback mode so seeded layers don't crash.
  const rng = config?.rng ?? {
    next: Math.random,
    int: (a: number, b: number) => Math.floor(Math.random() * (b - a + 1)) + a,
    pick: (arr: any[]) => arr[Math.floor(Math.random() * arr.length)],
    weighted: (items: any[], weights: number[]) => items[0],
    chance: (p: number) => Math.random() < p,
  };

  // Subtle tilt parallax — a few px on desktop mouse, gyro on phones with permission.
  const tilt = useTiltParallax({ intensity: 1, disabled: fadeOut });

  return (
    <motion.div
      aria-hidden
      className="absolute inset-0 overflow-hidden pointer-events-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: fadeOut ? 0 : 1 }}
      transition={{ duration: fadeOut ? 1.2 : 1.6, ease: [0.22, 0.61, 0.36, 1] }}
    >
      <BaseGradientLayer palette={palette} />

      {/* Parallax stack — only the seeded particle layers translate. */}
      <div
        className="absolute inset-0"
        style={{
          transform: `translate3d(${tilt.x * 12}px, ${tilt.y * 8}px, 0)`,
          willChange: 'transform',
          transition: 'transform 0.05s linear',
        }}
      >
        {trimmed.map((id) => {
          const Layer = BACKDROP_REGISTRY[id];
          if (!Layer) return null;
          return (
            <Layer
              key={id}
              palette={palette}
              rng={rng as any}
              density={density}
              isMobile={isMobile}
              hueShift={hueShift}
            />
          );
        })}
      </div>

      {/* Film grain is always nice as a top sheen — only re-add if not already chosen */}
      {!trimmed.includes('filmGrain') && <FilmGrainLayer />}

      {/* Phase 3 — handcrafted texture: subtle paper grain + soft light leaks.
          These sit above particles but below the vignette so the whole frame
          feels printed/photographed rather than rendered. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-[0.18]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.96  0 0 0 0 0.93  0 0 0 0 0.86  0 0 0 0.6 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
          backgroundSize: '220px 220px',
        }}
      />
      <motion.div
        aria-hidden
        className="absolute inset-0 pointer-events-none mix-blend-screen"
        initial={{ opacity: 0 }}
        animate={{ opacity: fadeOut ? 0 : 0.35 }}
        transition={{ duration: 2.4, ease: [0.22, 0.61, 0.36, 1] }}
        style={{
          background:
            'radial-gradient(60% 38% at 18% 12%, hsla(38, 90%, 78%, 0.22), transparent 70%), radial-gradient(45% 35% at 85% 88%, hsla(20, 85%, 70%, 0.18), transparent 72%)',
        }}
      />

      <VignetteLayer palette={palette} />
    </motion.div>
  );
}
